import { create } from 'zustand';
import { toast } from './toast';

interface ConnectionState {
  /** False once a request could not reach the server, or the browser says offline. */
  online: boolean;
  /** While offline, when the server last answered, in ms. Null while online. */
  offlineSince: number | null;
}

export const useConnection = create<ConnectionState>(() => ({ online: true, offlineSince: null }));

/**
 * When the server last answered, kept in the browser so a page reloaded with
 * no line still says how long it has been without one. Only a time; nothing
 * about anybody's work.
 */
const LAST_REACHED_KEY = 'poultry.lastReachedAt';
/** Written at most this often; every request would be far too many. */
const LAST_REACHED_WRITE_MS = 30_000;

let lastReached: number | null = readLastReached();
let lastWritten = 0;

function readLastReached(): number | null {
  try {
    const at = Number(localStorage.getItem(LAST_REACHED_KEY));
    return Number.isFinite(at) && at > 0 ? at : null;
  } catch {
    return null;
  }
}

function writeLastReached(at: number): void {
  lastWritten = Date.now();
  try {
    localStorage.setItem(LAST_REACHED_KEY, String(at));
  } catch {
    // Private mode: the time is kept for this page only.
  }
}

/**
 * Behind nginx, a stopped API still answers — with 502 or 504 from the proxy.
 * Those mean "the server is not there", not "the server said no".
 */
export function isGatewayFailure(status: number): boolean {
  return status === 502 || status === 504;
}

const CHECK_EVERY_MS = 10_000;

/** What else to say when the line comes back — that saved work is going now. Set by the outbox. */
let backOnlineWords: () => string = () => '';
export function setBackOnlineWords(words: () => string): void {
  backOnlineWords = words;
}
let checkTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Called by the API client. A request that got any answer at all proves the
 * server is reachable; one that failed at the network level proves it is not.
 */
export function reportReachable(reachable: boolean): void {
  if (reachable) {
    lastReached = Date.now();
    if (lastReached - lastWritten >= LAST_REACHED_WRITE_MS) writeLastReached(lastReached);
  }
  const was = useConnection.getState().online;
  if (reachable === was) return;

  if (!reachable) {
    if (lastReached) writeLastReached(lastReached);
    useConnection.setState({ online: false, offlineSince: lastReached ?? Date.now() });
  } else {
    useConnection.setState({ online: true, offlineSince: null });
  }

  if (!reachable) {
    // Keep asking, so the notice clears by itself when the network returns
    // instead of waiting for the user to try something.
    checkTimer ??= setInterval(() => void probe(), CHECK_EVERY_MS);
  } else {
    if (checkTimer !== null) clearInterval(checkTimer);
    checkTimer = null;
    toast.success(`Connection is back.${backOnlineWords()}`);
  }
}

async function probe(): Promise<void> {
  try {
    const res = await fetch('/api/v1/health', { credentials: 'include' });
    reportReachable(!isGatewayFailure(res.status));
  } catch {
    // Still down; the interval tries again.
  }
}

/**
 * Asks the server itself, and believes the answer either way. For the
 * browser's "offline": that means no internet, and a server on this PC or the
 * farm's own network can still be reached without it.
 */
async function check(): Promise<void> {
  try {
    const res = await fetch('/api/v1/health', { credentials: 'include' });
    reportReachable(!isGatewayFailure(res.status));
  } catch {
    reportReachable(false);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => void check());
  window.addEventListener('online', () => void probe());
}
